import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Icon, Input, Modal, Notice } from '../design/index.js'
import { useApi, type SavedView } from '../app/api.js'
import { messageFor } from './feedback.js'

/**
 * "Create View" (docs/02 §3): a name for the way someone always looks at a
 * list.
 *
 * A view carries filter VALUES and column choices, both checked against the
 * definition by the server. Nothing here composes a query.
 */

export interface SavedViewsProps {
  readonly scope: string
  /** What would be saved if the person saved right now. */
  readonly filters: Readonly<Record<string, string>>
  readonly columns: readonly string[]
  readonly onApply: (view: SavedView) => void
  /** Applied once, when the list opens with no query of its own. */
  readonly onDefault?: (view: SavedView) => void
}

/**
 * Whether what is on screen still matches the view it was opened from.
 *
 * Compared on the filters and the chosen columns, which are the only things a
 * view stores. Order does not count — the same three filters set in a different
 * sequence are the same view.
 */
const sameAsView = (
  view: SavedView,
  filters: Readonly<Record<string, string>>,
  columns: readonly string[],
): boolean => {
  const live = Object.entries(filters).filter(([, v]) => v !== '').sort()
  const saved = Object.entries(view.filters).filter(([, v]) => v !== '').sort()
  if (live.length !== saved.length) return false
  if (live.some(([k, v], i) => saved[i]?.[0] !== k || saved[i]?.[1] !== v)) return false
  // No column choice on either side is the same as matching column choices.
  if (view.columns.length === 0 && columns.length === 0) return true
  return view.columns.length === columns.length
    && [...view.columns].sort().join() === [...columns].sort().join()
}

export const SavedViews = ({ scope, filters, columns, onApply, onDefault }: SavedViewsProps) => {
  const api = useApi()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  /** Which view the screen was last opened from, so edits to it can be shown. */
  const [appliedId, setAppliedId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState({ name: '', shared: false, isDefault: false })
  const [error, setError] = useState<string | null>(null)
  const appliedDefault = useRef(false)

  const views = useQuery({
    queryKey: ['views', api.companyId, scope],
    queryFn: () => api.savedViews(scope),
    enabled: scope !== '',
  })

  // The default opens the list, once, and only when nothing was asked for.
  useEffect(() => {
    if (appliedDefault.current || !onDefault) return
    const chosen = (views.data ?? []).find((v) => v.isDefault && v.mine)
    if (!chosen) return
    appliedDefault.current = true
    setAppliedId(chosen.id)
    onDefault(chosen)
  }, [views.data, onDefault])

  const save = useMutation({
    mutationFn: () =>
      api.saveView({
        scope,
        name: draft.name,
        shared: draft.shared,
        isDefault: draft.isDefault,
        filters,
        columns: [...columns],
      }),
    onSuccess: (next) => {
      client.setQueryData(['views', api.companyId, scope], next)
      setSaving(false)
      setDraft({ name: '', shared: false, isDefault: false })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (view: SavedView) => api.deleteView(view.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['views', api.companyId, scope] })
      setError(null)
    },
    onError: (err) => setError(messageFor(err)),
  })

  const rows = views.data ?? []
  const applied = appliedId === null ? undefined : rows.find((v) => v.id === appliedId)
  // docs/15 R-9.7 — a view with unapplied edits renders with a leading asterisk,
  // so nobody exports "All RFQs" believing it is still all of them.
  const dirty = applied !== undefined && !sameAsView(applied, filters, columns)

  return (
    <>
      <Button kind="ghost" icon={<Icon name="star" size={18} />} onClick={() => setOpen(true)}>
        {applied
          ? `${dirty ? '*' : ''}${applied.name}`
          : `Views${rows.length > 0 ? ` (${rows.length})` : ''}`}
      </Button>
      {dirty ? (
        <span className="view-dirty" title="This view has changes that have not been saved.">
          edited
        </span>
      ) : null}

      {open ? (
        <Modal
          title="Saved views"
          onClose={() => setOpen(false)}
          footerLeft={
            <Button
              kind="ghost"
              icon={<Icon name="plus" size={18} />}
              onClick={() => {
                setSaving(true)
                setOpen(false)
              }}
            >
              Save what is on screen
            </Button>
          }
          actions={<Button kind="ghost" onClick={() => setOpen(false)}>Close</Button>}
        >
          {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}

          {rows.length === 0 ? (
            <p>
              No views yet. Set the filters and columns the way you want them, then save them here under a
              name — yours alone, or shared with everyone.
            </p>
          ) : (
            <ul className="views">
              {rows.map((view) => (
                <li className="view" key={view.id}>
                  <button
                    type="button"
                    className="view__open"
                    onClick={() => {
                      onApply(view)
                      setAppliedId(view.id)
                      setOpen(false)
                    }}
                  >
                    <span className="view__name">{view.name}</span>
                    <span className="view__meta">
                      {Object.keys(view.filters).length} filter
                      {Object.keys(view.filters).length === 1 ? '' : 's'}
                      {view.columns.length > 0 ? ` · ${view.columns.length} columns` : ''}
                      {view.shared ? ` · shared by ${view.ownerName}` : ' · yours'}
                      {view.isDefault ? ' · opens by default' : ''}
                    </span>
                  </button>
                  {view.mine ? (
                    <Button kind="ghost" disabled={remove.isPending} onClick={() => remove.mutate(view)}>
                      Delete
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      ) : null}

      {saving ? (
        <Modal
          title="Save this view"
          onClose={() => setSaving(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setSaving(false)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={save.isPending || draft.name.trim().length < 2}
                onClick={() => save.mutate()}
              >
                Save it
              </Button>
            </>
          }
        >
          {error ? <div style={{ marginBottom: 14 }}><Notice kind="bad">{error}</Notice></div> : null}

          <Field label="Call it" required hint="Something you will recognise: “Open orders, this month”.">
            <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          </Field>

          <label className="column-choice">
            <input
              type="checkbox"
              checked={draft.shared}
              onChange={(e) => setDraft({ ...draft, shared: e.currentTarget.checked })}
            />
            <span>Share it with everyone here — they can open it; only you can change it.</span>
          </label>

          <label className="column-choice">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(e) => setDraft({ ...draft, isDefault: e.currentTarget.checked })}
            />
            <span>Open this list on this view.</span>
          </label>

          <p className="muted" style={{ marginTop: 14 }}>
            It saves {Object.keys(filters).length} filter{Object.keys(filters).length === 1 ? '' : 's'}
            {columns.length > 0 ? ` and ${columns.length} chosen columns` : ' and the standard columns'}.
          </p>
        </Modal>
      ) : null}
    </>
  )
}
